import { useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { isEmailadres } from "../emailadres";
import { DateField } from "./DateField";

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Optional leading glyph rendered inside the input (e.g. a user/lock icon on the login form). */
  icon?: ReactNode;
  /** Een melding onder het veld. Zet zelf `aria-invalid` niet: dat doet het veld. */
  error?: ReactNode;
  /**
   * De melding bij een `type="email"`-veld dat geen adres bevat. Een Nederlandse standaard, zodat
   * elk e-mailveld in het systeem meteen controleert zonder dat elke aanroeper iets hoeft door te
   * geven; geef hem mee om te vertalen.
   */
  invalidEmailMessage?: string;
}

/**
 * A labelled text input — the form-field vocabulary. Token-styled.
 *
 * Een `type="email"`-veld controleert zichzelf: zodra je het veld verlaat met iets dat geen adres is,
 * staat de melding eronder. Dat zit hier en niet in elke aanroeper, zodat het overal in het systeem
 * hetzelfde werkt. Tijdens het typen zwijgt het veld — pas bij verlaten, of zodra het opnieuw goed is.
 *
 * De melding staat buiten het label en is via {@code aria-describedby} aan het veld gekoppeld: in het
 * label zou hij deel worden van de naam van het veld ("E-mailadres Vul een geldig e-mailadres in").
 *
 * <p>Een {@code type="date"}-veld wordt het eigen datumveld met kalender ({@link DateField}) in plaats van
 * de datumkiezer van de browser. De waarde blijft {@code jjjj-mm-dd} en {@code onChange} levert nog steeds
 * {@code target.value}, dus daar merkt de aanroeper niets van.
 */
export function Field({ label, icon, error, invalidEmailMessage, ...rest }: FieldProps) {
  const [verlaten, setVerlaten] = useState(false);
  const isDatum = rest.type === "date";
  const eigenId = useId();
  const inputId = rest.id ?? eigenId;
  const meldingId = `${inputId}-melding`;
  const waarde = typeof rest.value === "string" ? rest.value : "";
  const emailFout =
    rest.type === "email" && verlaten && waarde.trim() !== "" && !isEmailadres(waarde);
  const melding = error ?? (emailFout ? (invalidEmailMessage ?? "Vul een geldig e-mailadres in.") : null);

  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    ...rest,
    id: inputId,
    "aria-invalid": melding ? true : rest["aria-invalid"],
    "aria-describedby": melding
      ? [rest["aria-describedby"], meldingId].filter(Boolean).join(" ")
      : rest["aria-describedby"],
    onBlur: (e) => {
      setVerlaten(true);
      rest.onBlur?.(e);
    },
  };

  if (isDatum) {
    return (
      <DateField
        label={label}
        id={inputId}
        name={rest.name}
        value={waarde}
        min={typeof rest.min === "string" ? rest.min : undefined}
        max={typeof rest.max === "string" ? rest.max : undefined}
        required={rest.required}
        disabled={rest.disabled}
        readOnly={rest.readOnly}
        error={melding}
        className={rest.className}
        aria-label={rest["aria-label"]}
        aria-describedby={rest["aria-describedby"]}
        onChange={rest.onChange}
        onBlur={inputProps.onBlur}
      />
    );
  }

  return (
    <div className="fui-field">
      <label className="fui-field__label" htmlFor={inputId}>
        {label}
      </label>
      {icon ? (
        <span className="fui-field__control">
          <span className="fui-field__icon" aria-hidden="true">
            {icon}
          </span>
          <input className="fui-field__input fui-field__input--with-icon" {...inputProps} />
        </span>
      ) : (
        <input className="fui-field__input" {...inputProps} />
      )}
      {melding && (
        <span className="fui-field__error" id={meldingId}>
          {melding}
        </span>
      )}
    </div>
  );
}
